import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Home, Building2, MapPin, Utensils, ClipboardList, TrendingUp, Calendar, AlertTriangle, UserX, User, UserCheck, Baby, Crown, BookOpen, Heart, Tag } from 'lucide-react';

export default function DashboardPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // Auto-refresh stats every 30 seconds for realtime updates
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Families', value: stats?.total_families || 0, icon: Home, color: 'bg-blue-500', description: 'HOF registered', href: '/families' },
    { title: 'Total Members', value: stats?.total_members || 0, icon: Users, color: 'bg-emerald-500', description: 'Jamaat members', href: '/members' },
    { title: 'Mardo', value: stats?.mardo_count || 0, icon: User, color: 'bg-cyan-600', description: 'Misaq Done Gents', href: '/members?gender=Male&misaq=Done' },
    { title: 'Bairao', value: stats?.bairao_count || 0, icon: User, color: 'bg-rose-500', description: 'Misaq Done Ladies', href: '/members?gender=Female&misaq=Done' },
    { title: 'Farzando', value: stats?.farzando_count || 0, icon: Baby, color: 'bg-amber-500', description: 'Misaq not Done', href: '/members?misaq=Not Done' },
    { title: 'Mashaikh', value: stats?.mashaikh_count || 0, icon: Crown, color: 'bg-teal-600', description: 'Shaikh families', href: '/drilldown/mashaikh/list' },
    { title: 'Mafasih', value: stats?.mafasih_count || 0, icon: BookOpen, color: 'bg-violet-600', description: 'Mulla families', href: '/drilldown/mafasih/list' },
    { title: 'Non-Tagged', value: stats?.non_tagged || 0, icon: Tag, color: 'bg-orange-500', description: 'Unassigned families', href: '/drilldown/non-tagged/families' },
    { title: 'Mohallas', value: stats?.total_mohallas || 0, icon: Building2, color: 'bg-purple-500', description: 'Active mohallas', href: '/mohallas' },
    { title: 'Sectors', value: stats?.total_sectors || 0, icon: MapPin, color: 'bg-indigo-500', description: 'Active sectors', href: '/sectors' },
    { title: 'FMB Going', value: stats?.fmb_going || 0, icon: Utensils, color: 'bg-green-500', description: 'Thaali receivers', href: '/drilldown/fmb/Going' },
    { title: 'FMB Not Going', value: stats?.fmb_not_going || 0, icon: Utensils, color: 'bg-red-500', description: 'Not receiving', href: '/drilldown/fmb/Not Going' },
    { title: "Today's Changes", value: stats?.today_changes || 0, icon: TrendingUp, color: 'bg-slate-600', description: 'Updates today', href: '/audit' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-navy to-blue-700 rounded-xl p-4 sm:p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-heading font-semibold">
              Welcome, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-blue-100 text-xs sm:text-base mt-1">
              Anjuman-e-Saifee Jamaat Management
            </p>
          </div>
          <div className="flex items-center gap-2 text-blue-100 text-xs sm:text-sm">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="sm:hidden">{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="card-hover border-0 shadow-card cursor-pointer transition-all hover:shadow-lg active:scale-[0.98]" 
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => stat.href && navigate(stat.href)}
          >
            <CardContent className="p-3 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-sm text-slate-500 font-medium truncate">{stat.title}</p>
                  <p className="text-xl sm:text-3xl font-heading font-bold text-slate-800 mt-0.5 sm:mt-1">
                    {stat.value.toLocaleString('en-IN')}
                  </p>
                  <p className="text-[9px] sm:text-xs text-slate-400 mt-0.5 sm:mt-1 hidden sm:block">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-2 sm:p-3 rounded-lg sm:rounded-xl shadow-sm self-start`}>
                  <stat.icon className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Mohalla Distribution */}
      {stats?.mohalla_stats && stats.mohalla_stats.length > 0 && (
        <Card className="border-0 shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-navy" />
              Mohalla-wise Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {stats.mohalla_stats.filter(m => m._id).map((mohalla, index) => (
                <div 
                  key={index} 
                  className="bg-slate-50 rounded-lg p-4 border border-slate-100 hover:border-gold/50 transition-colors cursor-pointer hover:shadow-md"
                  data-testid={`mohalla-stat-${index}`}
                  onClick={() => navigate(`/drilldown/mohalla/${encodeURIComponent(mohalla._id)}`)}
                >
                  <p className="text-sm font-medium text-slate-600 truncate">{mohalla._id || 'Unassigned'}</p>
                  <p className="text-2xl font-heading font-bold text-slate-800">{mohalla.count}</p>
                  <p className="text-xs text-slate-400">families</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sector-wise Distribution */}
      {stats?.sector_stats && stats.sector_stats.length > 0 && (
        <Card className="border-0 shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gold" />
              Sector-wise Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {stats.sector_stats.map((sector, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-100 hover:shadow-sm transition-all cursor-pointer"
                  data-testid={`sector-stat-${index}`}
                  onClick={() => navigate(`/drilldown/sector/${encodeURIComponent(sector._id?.sector)}`)}
                >
                  <p className="text-xs font-medium text-slate-500 truncate">{sector._id?.mohalla?.replace(' MOHALLA', '')}</p>
                  <p className="text-sm font-semibold text-slate-700">{sector._id?.sector}</p>
                  <p className="text-lg font-heading font-bold text-navy">{sector.count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="border-0 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction href="/families" icon={Home} label="Manage Families" color="bg-blue-100 text-blue-700" />
            <QuickAction href="/members" icon={Users} label="View Members" color="bg-emerald-100 text-emerald-700" />
            <QuickAction href="/fmb" icon={Utensils} label="FMB Thaali" color="bg-amber-100 text-amber-700" />
            <QuickAction href="/audit" icon={ClipboardList} label="Audit Logs" color="bg-purple-100 text-purple-700" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QuickAction({ href, icon: Icon, label, color }) {
  return (
    <a
      href={href}
      className={`flex items-center gap-3 p-4 rounded-xl ${color} hover:opacity-90 transition-all hover:shadow-md`}
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium text-sm">{label}</span>
    </a>
  );
}
